'use strict';

import React, { useState, useContext, useEffect } from 'react';
import { LangContext } from '../App';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import api from '../services/api';

const T = {
  fr: {
    title: 'Mes évaluations',
    sousTitre: 'Avis échangés après chaque transaction (location de terre, chasse, guide).',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger vos évaluations.',
    moyenne: 'Note moyenne',
    sur5: '/ 5',
    aucuneEval: 'Aucune évaluation pour le moment.',
    tabRecues: 'Reçues',
    tabDonnees: 'Données',
    badge: {
      publiee: 'Publiée',
      signalee: 'Signalée',
      masquee: 'Masquée',
    },
    directionLabel: {
      chasseur_vers_proprietaire: 'Chasseur → Propriétaire',
      proprietaire_vers_chasseur: 'Propriétaire → Chasseur',
    },
    de: 'De',
    a: 'À',
    sansCommentaire: '(Sans commentaire)',
  },
  en: {
    title: 'My reviews',
    sousTitre: 'Feedback exchanged after each transaction (land rental, hunt, guide).',
    chargement: 'Loading…',
    erreur: 'Could not load your reviews.',
    moyenne: 'Average rating',
    sur5: '/ 5',
    aucuneEval: 'No reviews yet.',
    tabRecues: 'Received',
    tabDonnees: 'Given',
    badge: {
      publiee: 'Published',
      signalee: 'Flagged',
      masquee: 'Hidden',
    },
    directionLabel: {
      chasseur_vers_proprietaire: 'Hunter → Owner',
      proprietaire_vers_chasseur: 'Owner → Hunter',
    },
    de: 'From',
    a: 'To',
    sansCommentaire: '(No comment)',
  },
};

const OR = 'var(--couleur-or)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.1)';

function StarRow({ value }) {
  // Five stars rendered as solid/empty glyphs — keeps the visual compact and
  // avoids pulling in another icon dep for one screen.
  return (
    <span
      aria-label={`${value}/5`}
      style={{ color: OR, letterSpacing: '0.1em', fontSize: '0.95rem' }}
    >
      {[1, 2, 3, 4, 5].map((i) => (i <= value ? '★' : '☆')).join('')}
    </span>
  );
}

function CarteEvaluation({ ev, role, t, lang }) {
  const date = ev.createdAt
    ? new Date(ev.createdAt).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '—';
  // For the "received" view the counterpart is the evaluator; for "given" it's the evaluatee.
  const counterpart = role === 'received' ? ev.evaluateur : ev.evalue;
  const counterpartLabel = role === 'received' ? t.de : t.a;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${BORDURE}`,
        borderRadius: '10px',
        padding: '1rem 1.2rem',
        display: 'grid',
        gap: '0.6rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '0.8rem',
          flexWrap: 'wrap',
        }}
      >
        <StarRow value={ev.overall_rating} />
        <span style={{ fontSize: '0.72rem', color: TEXTE_DIM }}>{date}</span>
      </div>

      <div
        style={{
          display: 'flex',
          gap: '0.6rem',
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: '0.78rem',
          color: TEXTE_DIM,
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.4)' }}>{counterpartLabel}</span>
        <span style={{ color: TEXTE, fontWeight: 600 }}>{counterpart?.name || '—'}</span>
        <span
          style={{
            fontSize: '0.65rem',
            color: 'rgba(255,255,255,0.45)',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            background: 'rgba(255,255,255,0.05)',
            padding: '2px 8px',
            borderRadius: '20px',
          }}
        >
          {t.directionLabel[ev.direction] || ev.direction}
        </span>
        {ev.moderation_status !== 'publiee' && (
          <span
            style={{
              fontSize: '0.65rem',
              color: ev.moderation_status === 'masquee' ? 'var(--couleur-texte-erreur)' : OR,
              background:
                ev.moderation_status === 'masquee'
                  ? 'rgba(200,50,50,0.12)'
                  : 'rgba(201,162,39,0.12)',
              border: `1px solid ${ev.moderation_status === 'masquee' ? 'rgba(200,50,50,0.4)' : 'rgba(201,162,39,0.4)'}`,
              padding: '2px 8px',
              borderRadius: '20px',
            }}
          >
            {t.badge[ev.moderation_status] || ev.moderation_status}
          </span>
        )}
      </div>

      <div
        style={{
          fontSize: '0.88rem',
          color: ev.comment ? TEXTE : TEXTE_DIM,
          fontStyle: ev.comment ? 'normal' : 'italic',
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
        }}
      >
        {ev.comment || t.sansCommentaire}
      </div>
    </div>
  );
}

export default function MyReviews() {
  const { lang } = useContext(LangContext);
  const t = T[lang];
  const [data, setData] = useState(null);
  const [chargement, setCharge] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [tab, setTab] = useState('received');

  useEffect(() => {
    let annule = false;
    api
      .get('/me/reviews')
      .then((r) => {
        if (!annule) {
          setData(r.data);
          setErreur(false);
        }
      })
      .catch(() => {
        if (!annule) setErreur(true);
      })
      .finally(() => {
        if (!annule) setCharge(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  const summary = data?.summary || { received_count: 0, given_count: 0, average_rating: null };
  const items = tab === 'received' ? data?.received || [] : data?.given || [];

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', paddingBottom: '4rem' }}>
        <PageHeader icon="⭐" title={t.title} subtitle={t.sousTitre} />

        {/* Headline — average rating + counts. Always shown even if empty so
            users get a clear "0 reviews so far" baseline. */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '0.8rem',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '10px',
              padding: '0.95rem 1.1rem',
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                color: TEXTE_DIM,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t.moyenne}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: OR, marginTop: '0.3rem' }}>
              {summary.average_rating != null ? `${summary.average_rating} ${t.sur5}` : '—'}
            </div>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '10px',
              padding: '0.95rem 1.1rem',
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                color: TEXTE_DIM,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t.tabRecues}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: TEXTE, marginTop: '0.3rem' }}>
              {summary.received_count}
            </div>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '10px',
              padding: '0.95rem 1.1rem',
            }}
          >
            <div
              style={{
                fontSize: '0.7rem',
                color: TEXTE_DIM,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t.tabDonnees}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: TEXTE, marginTop: '0.3rem' }}>
              {summary.given_count}
            </div>
          </div>
        </div>

        {/* Tabs — Received / Given. Two-button toggle, no router state, simple. */}
        <div
          role="tablist"
          style={{
            display: 'inline-flex',
            background: 'rgba(255,255,255,0.04)',
            border: `1px solid ${BORDURE}`,
            borderRadius: '8px',
            padding: '3px',
            marginBottom: '1rem',
          }}
        >
          {[
            { key: 'received', label: t.tabRecues },
            { key: 'given', label: t.tabDonnees },
          ].map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              style={{
                background: tab === key ? 'rgba(255,255,255,0.08)' : 'transparent',
                color: tab === key ? TEXTE : TEXTE_DIM,
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.9rem',
                fontSize: '0.82rem',
                fontWeight: tab === key ? 600 : 500,
                cursor: 'pointer',
                transition: 'background 0.15s, color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        {chargement && (
          <div
            style={{
              color: TEXTE_DIM,
              fontSize: '0.85rem',
              padding: '1.2rem',
              textAlign: 'center',
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '8px',
            }}
          >
            {t.chargement}
          </div>
        )}

        {!chargement && erreur && (
          <div
            style={{
              background: 'rgba(200,50,50,0.1)',
              border: '1px solid rgba(200,50,50,0.35)',
              borderRadius: '8px',
              padding: '0.8rem 1rem',
              color: 'var(--couleur-texte-erreur)',
              fontSize: '0.85rem',
            }}
          >
            ⚠ {t.erreur}
          </div>
        )}

        {!chargement && !erreur && items.length === 0 && (
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(184,134,11,0.4)',
              borderRadius: '8px',
              padding: '1.4rem 1rem',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.4)',
              fontSize: '0.88rem',
            }}
          >
            {t.aucuneEval}
          </div>
        )}

        {!chargement && !erreur && items.length > 0 && (
          <div style={{ display: 'grid', gap: '0.8rem' }}>
            {items.map((ev) => (
              <CarteEvaluation key={ev.id} ev={ev} role={tab} t={t} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </PrivateLayout>
  );
}
